import ArrowRight from "@/assets/icons/ArrowRight";
import Clock from "@/assets/icons/Clock";
import Disco from "@/assets/icons/Disco";
import Lock from "@/assets/icons/Lock";
import Pass from "@/assets/icons/Pass";
import WarningIcon from "@/assets/icons/Warning";
import AppButton from "@/components/shared/AppButton";
import { Card } from "@/components/shared/Card";
import { Box } from "@/components/ui/box";
import { HStack } from "@/components/ui/hstack";
import { Skeleton, SkeletonText } from "@/components/ui/skeleton";
import { Text } from "@/components/ui/text";
import { VStack } from "@/components/ui/vstack";
import { formatToDollars } from "@/utils/etc";
import { T, useT } from "@transifex/react";
import type { PassForSale, PassSpecifications } from "ipctg-inflow-types/InFlowAPIMisc";
import { ValidForIntervalPeriod } from "ipctg-inflow-types/InFlowEnums";
import { FlatList } from "react-native";

interface FlexPassCardProps {
  onPurchase?: () => void;
  pass: PassForSale;
}

function FlexParkCard({ pass, onPurchase }: Readonly<FlexPassCardProps>) {
  const t = useT();
  return (
    <Card className="py-[24px]">
      <VStack className="gap-[12px]">
        <Text className="font-[700] leading-[22px] text-[16px] text-typography-900">{pass.passName}</Text>
        {pass.passSpecifications ? (
          <>
            <ValidForLineItem passSpecifications={pass.passSpecifications} />
            <HStack className="gap-[4px] items-center">
              <Clock height={20} fill="text-outline-800" width={20} />
              <Text className="font-[400] leading-[19px] text-[14px]/[19px] text-typography-900">
                {<RedemptionLineItem passSpecifications={pass.passSpecifications} />}
              </Text>
            </HStack>
            {pass.passSpecifications.subjectToEventRestrictions && (
              <HStack className="gap-[4px] items-center">
                <Disco height={20} fill="text-outline-800" width={20} />
                <Text className="font-[400] leading-[19px] text-[14px]/[19px] text-typography-900">
                  <T _str="Subject to event related restrictions." />
                </Text>
              </HStack>
            )}
            {pass.passSpecifications.requiresPassphrase && (
              <HStack className="gap-[4px] items-center">
                <Lock height={20} fill="text-outline-800" width={20} />
                <Text className="font-[400] leading-[19px] text-[14px]/[19px] text-typography-900">
                  <T _str="Requires passphrase to purchase" />
                </Text>
              </HStack>
            )}
          </>
        ) : pass.passNotes ? (
          <FlatList
            data={pass.passNotes}
            renderItem={({ item }) => <Text className="font-[400] leading-[20px] text-[14px] text-typography-900">{`\u2022 ${item}`}</Text>}
          />
        ) : (
          <></>
        )}
      </VStack>
      {pass.showNotRedeemableWarning && (
        <HStack className="bg-warning-100 border border-warning-300 gap-[10px] items-center px-[8px] py-[12px] rounded-[6px]">
          <WarningIcon height={20} fill="text-warning-900" width={20} />
          <Box className="flex-1">
            <Text className="font-[400] text-[12px]/[19px] text-warning-900">
              <T _str="This pass is available for purchase but is not eligible for immediate use." />
            </Text>
          </Box>
        </HStack>
      )}

      {onPurchase && (
        <AppButton icon={ArrowRight} iconPosition="after" onPress={onPurchase} title={t("Purchase - {value}", { value: formatToDollars(pass.amount) })} />
      )}
    </Card>
  );
}

export function ValidForLineItem(props: { passSpecifications: PassSpecifications; uses?: number }) {
  const { maxUses, validTo } = props.passSpecifications;

  const formattedDate = new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(new Date(validTo));

  return (
    <HStack className="gap-[4px] items-center">
      <Pass height={20} fill="text-outline-800" width={20} />
      <Text className="font-[400] leading-[19px] text-[14px]/[19px] text-typography-900">
        <T _str="{count, plural, one {# pass} other {# passes}} valid until {expiryDate}" count={maxUses - (props.uses || 0)} expiryDate={formattedDate} />
      </Text>
    </HStack>
  );
}

export function RedemptionLineItem(props: { passSpecifications: PassSpecifications }) {
  const redemptionConfig = props.passSpecifications.redemptionConfig;
  switch (redemptionConfig.type) {
    case "ValidFor": {
      const count = redemptionConfig.intervalCount;
      const period = redemptionConfig?.intervalPeriod;

      switch (period) {
        case ValidForIntervalPeriod.Minute:
          return <T _str="{count, plural, one {Park for up to # minute per session} other {Park for up to # mins per session}}" count={count} />;
        case ValidForIntervalPeriod.Hour:
          return <T _str="{count, plural, one {Park for up to # hour per session} other {Park for up to # hours per session}}" count={count} />;
        case ValidForIntervalPeriod.Day:
          return <T _str="{count, plural, one {Park for up to # day per session} other {Park for up to # days per session}}" count={count} />;
      }

      return `Park for up to ${count} ${period}${count > 1 ? "s" : ""} per session`;
    }
    case "ValidUntilDeparture":
    case "ValidUntilExpiration": {
      // Parse the expirationTime/departureTime string into a Date object
      const timeString = redemptionConfig.type === "ValidUntilExpiration" ? redemptionConfig.expirationTime : redemptionConfig.departureTime;

      if (!timeString) return <></>;
      const dateObj = new Date(`1970-01-01T${timeString}`);
      const formattedTime = new Intl.DateTimeFormat(undefined, {
        hour: "numeric",
        minute: "numeric",
      }).format(dateObj);
      if (redemptionConfig.type === "ValidUntilDeparture" && !redemptionConfig.nights) {
        return (
          <T
            _str="{count, plural, one {Park for # night, until {time}} other {Park for # nights, until {time}}}"
            count={redemptionConfig.nights}
            time={formattedTime}
          />
        );
      }

      return <T _str="Park until {time}" time={formattedTime} />;
    }
    default: {
      throw new Error("Unknown redemptionConfig type");
    }
  }
}

export function FlexParkCardSkeleton({ showEventRestrictionWarning }: { showEventRestrictionWarning: boolean }) {
  return (
    <Card className="py-[24px]">
      <VStack className="gap-[12px]">
        <SkeletonText className="h-[22px] w-[160px]" />
        <VStack className="gap-[10px]">
          <SkeletonText className="h-[22px] w-[160px]" />
          <SkeletonText className="h-[22px] w-[120px]" />
        </VStack>
      </VStack>

      {showEventRestrictionWarning && <Skeleton className="h-[48px] w-full rounded-[6px]" />}

      <Skeleton className="h-[40px] rounded-[6px]" />
    </Card>
  );
}

export default FlexParkCard;
